<template>
  <q-input
    v-bind="$attrs"
    v-on="$attrs"
    @update:model-value="onInputText"
  >
    <template #append>
      <q-spinner
        v-if="isAnalyzing"
        size="20px"
        color="primary"
      />
      <q-circular-progress
        v-else-if="isRecording"
        :value="mediaRecorderProgress"
        instant-feedback
        show-value
        :max="maxRecordingTime"
        track-color="transparent"
        class="text-primary cursor-pointer"
        @click="onStopTranscribingText"
      >
        <q-icon
          name="stop"
          size="18px"
        />
      </q-circular-progress>
      <template v-else>
        <q-btn
          v-if="showRefresh"
          icon="refresh"
          color="dark-variant"
          flat
          round
          @click="onRefresh"
        />
        <q-btn
          v-if="!disableVoiceInput"
          icon="mic"
          color="dark-variant"
          flat
          round
          :disable="offlineMode"
          @click="onTranscribeText"
        />
      </template>
    </template>
  </q-input>
</template>
<script>
import { callFirebaseFunction } from 'boot/firebase';
import { trim } from 'lodash';
import { mapState } from 'pinia';
import RecordRTC from 'recordrtc';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { aiErrorMessageKey } from 'src/utils/aiErrors';

export default {
  name: 'VoiceInput',

  mixins: [Interactions],

  inheritAttrs: false,

  props: {

    disableVoiceInput: {
      type: Boolean,
      default: false,
    },

    maxRecordingTime: {
      type: Number,
      default: 60000,
    },

    showRefresh: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'processing', 'refresh'],

  data() {
    return {
      isRecording: false,
      isAnalyzing: false,
      mediaStream: null,
      recorder: null,
      sampleRate: 16000,
      mediaRecorderProgress: 0,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'isSubscriptionPremium']),
    ...mapState(network, ['offlineMode']),

    processing() {
      return this.isRecording || this.isAnalyzing;
    },
  },

  watch: {
    processing(value) {
      this.$emit('processing', value);
    },
  },

  async beforeUnmount() {
    this.$emit('processing', false);
    if (this.isRecording) {
      this.mediaRecorderProgress = null;
      if (this.recorder) {
        this.recorder.stopRecording();
      }
      this.isRecording = false;
    }
  },

  methods: {

    onInputText($event) {
      this.$emit('update:modelValue', $event);
    }, // onInputText()

    onRefresh() {
      this.$emit('refresh');
    }, // onRefresh()

    async onStopTranscribingText() {
      if (!this.isRecording) {
        return;
      }
      this.mediaRecorderProgress = null;
      this.recorder.stopRecording(this.processRecordedAudio);
      this.isRecording = false;
    }, // onStopTranscribingText()

    async onTranscribeText() {
      if (this.isRecording) {
        return;
      }

      if (!this.isSubscriptionPremium) {
        this.notifyNegative(this.$t('common.errors.transcriptionPremiumFeature'));
        return;
      }

      if (trim(this.$attrs['model-value'])) {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('common.confirmOverwriteField'),
          () => {
            this.transcribeText();
          },
        );
      } else {
        this.transcribeText();
      }
    }, // onTranscribeText()

    async transcribeText() {
      this.isRecording = true;

      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

        this.recorder = new RecordRTC(this.mediaStream, {
          type: 'audio',
          mimeType: this.$q.platform.is.ios ? 'audio/webm' : 'audio/wav',
          recorderType: RecordRTC.StereoAudioRecorder,
          numberOfAudioChannels: 1,
          desiredSampRate: this.sampleRate,
          disableLogs: true,
        });

        this.recorder.startRecording();
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          this.notifyNegative(this.$t('common.errors.transcriptionPermissionDenied'));
        } else {
          this.notifyNegative(this.$t('common.errors.transcriptionFailed'));
          console.error(err);
        }

        this.isRecording = false;
        return;
      }

      this.mediaRecorderProgress = 0;
      const indicatorTimer = setInterval(() => {
        if (this.mediaRecorderProgress === null) {
          clearTimeout(indicatorTimer);
          return;
        }
        this.mediaRecorderProgress += 100;
        if (this.mediaRecorderProgress > this.maxRecordingTime) {
          this.onStopTranscribingText();
          clearTimeout(indicatorTimer);
        }
      }, 100);
    }, // transcribeText()

    async processRecordedAudio() {
      this.isAnalyzing = true;
      const audioBlob = this.recorder.getBlob();

      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);

      reader.onloadend = async () => {
        if (!this) {
          return;
        }

        try {
          const base64Audio = reader.result.split(',')[1];

          const response = await callFirebaseFunction(
            'transcribeAudio',
            { clinicId: this.userDetails.clinic, audio: base64Audio, sampleRate: this.sampleRate },
          );

          this.$emit('update:modelValue', response.data);
        } catch (err) {
          console.error(err);
          this.notifyNegative(this.$t(aiErrorMessageKey(err, 'common.errors.transcriptionFailed')));
        } finally {
          this.isAnalyzing = false;
        }
      };

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
      }
    }, // processRecordedAudio()
  },
};
</script>
